import { HttpClient } from '@angular/common/http';
import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, interval } from 'rxjs';
import { catchError, filter, switchMap } from 'rxjs/operators';

import { BroadcastChannelActions } from '@common/model/broadcast-channel-actions';
import { BroadcastChannelService } from '@common/services/broadcast-channel.service';

@Injectable({ providedIn: 'root' })
export class AppVersionService {
  private readonly document = inject(DOCUMENT);
  private readonly http = inject(HttpClient);
  private readonly broadcastChannelService = inject(BroadcastChannelService);

  private readonly currentMainSrc = this.document
    .querySelector<HTMLScriptElement>('script[src*="main-"]')
    ?.getAttribute('src') ?? null;

  private readonly updateAvailable = new BehaviorSubject(false);
  readonly isUpdateAvailable$ = this.updateAvailable.asObservable();

  start(): void {
    // Only poll in builds where Angular has hashed the main bundle (i.e. not ng serve)
    if (!this.currentMainSrc?.match(/main-\w+\.js/)) {
      return;
    }

    interval(5 * 60 * 1000)
      .pipe(
        switchMap(() =>
          this.http.get(`index.html?_=${Date.now()}`, {
            responseType: 'text'
          }).pipe(catchError(() => EMPTY))
        ),
        filter(html => {
          const match = html.match(/src="(main-\w+\.js)"/);
          return !!match && match[1] !== this.currentMainSrc;
        })
      )
      .subscribe(() => this.updateAvailable.next(true));
  }

  reload(): void {
    this.broadcastChannelService.sendMessage({ action: BroadcastChannelActions.channelTabsRefresh });
    window.location.reload();
  }
}
